import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Calendar, AlertCircle, CheckCircle2, Clock } from 'lucide-react';
import { getTasks, createTask, updateTask, deleteTask, getOverdueTasks, getLeads, getUsers } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

function Tasks() {
  const { t } = useLanguage();
  
  const priorityColors = {
    low: 'bg-gray-100 text-gray-700',
    normal: 'bg-blue-100 text-blue-700',
    high: 'bg-orange-100 text-orange-700',
    urgent: 'bg-red-100 text-red-700'
  };

  const priorityLabels = { 
    low: t('tasks.priorityLow'), 
    normal: t('tasks.priorityNormal'), 
    high: t('tasks.priorityHigh'), 
    urgent: t('tasks.priorityUrgent') 
  };

  const [tasks, setTasks] = useState([]);
  const [overdue, setOverdue] = useState([]);
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    title: '', description: '', due_date: '', priority: 'normal',
    lead_id: '', assigned_to: ''
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [tasksRes, overdueRes, leadsRes, usersRes] = await Promise.all([
        getTasks(), getOverdueTasks(), getLeads(), getUsers()
      ]);
      setTasks(tasksRes.data);
      setOverdue(overdueRes.data);
      setLeads(leadsRes.data);
      setUsers(usersRes.data);
    } catch (err) { console.error(err); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await updateTask(editing.id, form);
      } else {
        await createTask(form);
      }
      setShowModal(false);
      setEditing(null);
      setForm({ title: '', description: '', due_date: '', priority: 'normal', lead_id: '', assigned_to: '' });
      loadData();
    } catch (err) { alert(t('common.error') + ': ' + (err.response?.data?.error || err.message)); }
  };

  const handleEdit = (task) => {
    setEditing(task);
    setForm({
      title: task.title, description: task.description || '',
      due_date: task.due_date ? new Date(task.due_date).toISOString().slice(0, 16) : '',
      priority: task.priority, lead_id: task.lead_id || '', assigned_to: task.assigned_to || ''
    });
    setShowModal(true);
  };

  const handleStatusChange = async (task, status) => {
    try {
      await updateTask(task.id, { status });
      loadData();
    } catch (err) { alert(t('tasks.errorUpdateStatus')); }
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('tasks.confirmDelete'))) {
      try { await deleteTask(id); loadData(); } catch (err) { alert(t('tasks.errorDelete')); }
    }
  };

  const isOverdue = (dueDate) => new Date(dueDate) < new Date();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100">{t('tasks.title')}</h1>
          <p className="text-gray-500 dark:text-slate-400">{t('tasks.subtitle')}</p>
        </div>
        <button onClick={() => { setEditing(null); setForm({ title: '', description: '', due_date: '', priority: 'normal', lead_id: '', assigned_to: '' }); setShowModal(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 shadow-lg shadow-green-100 dark:shadow-none">
          <Plus size={18} /> {t('tasks.newTask')}
        </button>
      </div>

      {/* Overdue Tasks Alert */}
      {overdue.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle size={20} className="text-red-600 dark:text-red-400" />
            <h3 className="font-bold text-red-900 dark:text-red-300">{t('tasks.overdueCount', { count: overdue.length })}</h3>
          </div>
          <div className="space-y-2">
            {overdue.slice(0, 3).map(task => (
              <div key={task.id} className="flex items-center justify-between bg-white dark:bg-slate-800 p-3 rounded-xl">
                <div>
                  <p className="font-medium text-gray-900 dark:text-slate-100">{task.title}</p>
                  <p className="text-xs text-gray-500 dark:text-slate-400">{t('tasks.dueOn')} {new Date(task.due_date).toLocaleString()}</p>
                </div>
                <button onClick={() => handleStatusChange(task, 'completed')}
                  className="px-3 py-1 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700">
                  {t('tasks.complete')}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tasks List */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-50 dark:border-slate-700">
          <h2 className="text-lg font-bold text-gray-900 dark:text-slate-100">{t('tasks.allTasks')}</h2>
        </div>
        <div className="divide-y divide-gray-50 dark:divide-slate-700">
          {tasks.map(task => (
            <div key={task.id} className="p-4 hover:bg-gray-50 dark:hover:bg-slate-700 flex items-center justify-between">
              <div className="flex items-center gap-4 flex-1">
                <button onClick={() => handleStatusChange(task, task.status === 'completed' ? 'pending' : 'completed')}
                  className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    task.status === 'completed' ? 'bg-green-600 border-green-600' : 'border-gray-300 hover:border-green-500'
                  }`}>
                  {task.status === 'completed' && <CheckCircle2 size={14} className="text-white" />}
                </button>
                <div className="flex-1">
                  <p className={`font-medium ${task.status === 'completed' ? 'text-gray-400 line-through' : 'text-gray-900 dark:text-slate-100'}`}>
                    {task.title}
                  </p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 dark:text-slate-400">
                    <span className="flex items-center gap-1">
                      <Calendar size={12} />
                      {new Date(task.due_date).toLocaleString()}
                    </span>
                    {task.lead_name && <span>{t('tasks.lead')}: {task.lead_name}</span>}
                    {task.assigned_to_name && <span>{t('tasks.responsible')}: {task.assigned_to_name}</span>}
                  </div>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-bold ${priorityColors[task.priority]}`}>
                  {priorityLabels[task.priority]}
                </span>
              </div>
              <div className="flex gap-2 ml-4">
                <button onClick={() => handleEdit(task)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg"><Edit2 size={16} /></button>
                <button onClick={() => handleDelete(task.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
          {tasks.length === 0 && <p className="p-8 text-center text-gray-400 dark:text-slate-500">{t('tasks.noTasks')}</p>}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-md p-6 shadow-xl">
            <h2 className="text-lg font-bold mb-4 dark:text-slate-100">{editing ? t('tasks.editTask') : t('tasks.newTask')}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <input type="text" placeholder={`${t('tasks.titleField')} *`} value={form.title} onChange={e => setForm({...form, title: e.target.value})} required
                className="w-full p-2.5 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none" />
              <textarea placeholder={t('tasks.description')} value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3}
                className="w-full p-2.5 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none resize-none" />
              <input type="datetime-local" value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})} required
                className="w-full p-2.5 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none" />
              <select value={form.priority} onChange={e => setForm({...form, priority: e.target.value})}
                className="w-full p-2.5 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none">
                {Object.entries(priorityLabels).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
              <select value={form.lead_id} onChange={e => setForm({...form, lead_id: e.target.value})}
                className="w-full p-2.5 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none">
                <option value="">{t('tasks.noLinkedLead')}</option>
                {leads.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
              <select value={form.assigned_to} onChange={e => setForm({...form, assigned_to: e.target.value})}
                className="w-full p-2.5 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none">
                <option value="">{t('tasks.noResponsible')}</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
              <div className="flex gap-3 justify-end pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border border-gray-200 dark:border-slate-600 rounded-xl text-sm font-medium text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700">{t('common.cancel')}</button>
                <button type="submit" className="px-5 py-2 bg-green-600 text-white rounded-xl text-sm font-bold hover:bg-green-700">{t('common.save')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Tasks;